import { useState, useEffect, useCallback } from 'react';
import { eventRepository } from '../../../../domain/repositories';

/**
 * Custom hook to manage events data and pagination
 * @returns {Object} Events state and handlers
 */
export const useEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState(null);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [eventsData, pagination] = await Promise.all([
        currentPage === 0 
          ? eventRepository.getEventsFirstPage()
          : eventRepository.getEventsForPage(currentPage),
        eventRepository.getPaginationInfo(currentPage)
      ]);
      
      // Sort events by date (most recent first)
      const sortedEvents = eventsData.sort((a, b) => {
        const [dayA, monthA, yearA] = a.date.split('/').map(Number);
        const [dayB, monthB, yearB] = b.date.split('/').map(Number);
        
        // Compare year first
        if (yearB !== yearA) return yearB - yearA;
        // Then month
        if (monthB !== monthA) return monthB - monthA;
        // Finally day
        return dayB - dayA;
      });
      
      setEvents(sortedEvents);
      setPaginationInfo(pagination);
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Failed to load events. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleNextPage = () => {
    if (paginationInfo?.hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo?.hasPreviousPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const retry = () => {
    loadEvents();
  };

  return {
    events,
    loading,
    error,
    currentPage,
    paginationInfo,
    handleNextPage,
    handlePreviousPage,
    retry
  };
};
